import type { GovernancePolicyFormData } from '@experiences/interfaces';
import { Scope } from '@experiences/interfaces';
import { useShowDialog } from '@experiences/util';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { useTenantIdContext } from '../../../../../common/providers/TenantIdProvider';
import { getTenants } from '../../../../../services/organization/TenantService';
import {
    accountGlobalId,
    accountLogicalName,
} from '../../../../../store/selectors';
import {
    useStepHooks,
    useUiStepper,
} from '../../../../common/UiWizard';
import { TenantStatusConstants } from '../../../../tenants/TenantConstants';

const useGovernancePolicyCreateScopeStepViewModel = () => {
    const organizationId = useSelector(accountGlobalId);
    const accountName = useSelector(accountLogicalName);
    const { tenantId: scopedTenantId } = useTenantIdContext();
    const isTenantScoped = !!scopedTenantId;
    const { formatMessage: translate } = useIntl();
    const createDialog = useShowDialog();
    const { resetVisitedSteps } = useUiStepper();

    const {
        setValue,
        watch,
    } = useFormContext<GovernancePolicyFormData>();

    const tenantId = watch('tenantId') ?? '';

    const selectors = watch('selectors');
    const executableRule = watch('executableRule');
    const actorRule = watch('actorRule');

    const hasPolicyData = useMemo(() => {
        const hasSelectors = (selectors?.length ?? 0) > 0;
        const hasExecutables = (executableRule?.values?.length ?? 0) > 0;
        const hasActors = (actorRule?.values?.length ?? 0) > 0;
        return hasSelectors || hasExecutables || hasActors;
    }, [ selectors, executableRule, actorRule ]);

    const {
        data: allTenants, isLoading: isLoadingTenants,
    } = useSWR({
        url: 'tenants',
        organizationGuid: organizationId,
        accountName,
        includeTenantServices: true,
    }, getTenants);

    const tenants = useMemo(
        () => allTenants?.filter(t => t.status?.toUpperCase() === TenantStatusConstants.ENABLED),
        [ allTenants ],
    );

    // ApDropdown won't re-sync to `selectedValue` if the prop doesn't change, so
    // cancelling the dialog leaves the canceled selection visible. Bumping this
    // key on cancel forces a remount and reverts the dropdown to the form value.
    const [ dropdownKey, setDropdownKey ] = useState(0);

    const handleTenantChange = useCallback(async (event: CustomEvent) => {
        const selectedTenantId = event.detail as string;

        if (hasPolicyData) {
            const proceed = await createDialog({
                title: translate({ id: 'CLIENT_GOVERNANCE_SCOPE_CHANGE_WARNING_TITLE' }),
                body: (
                    <>
                        <p>
                            {translate({ id: 'CLIENT_GOVERNANCE_SCOPE_CHANGE_WARNING_MESSAGE_1' })}
                        </p>
                        <p>
                            {translate({ id: 'CLIENT_GOVERNANCE_SCOPE_CHANGE_WARNING_MESSAGE_2' })}
                        </p>
                    </>
                ),
                icon: 'warning',
                showCancel: true,
                primaryButtonText: translate({ id: 'CLIENT_CONFIRM_AND_RESET' }),
                cancelButtonText: translate({ id: 'CLIENT_CANCEL' }),
            });
            if (!proceed) {
                setDropdownKey((k) => k + 1);
                return;
            }
        }

        setValue('tenantId', selectedTenantId, { shouldDirty: true });
        setValue('scope', Scope.Tenant, { shouldDirty: true });
        setValue('selectors', [], { shouldDirty: true });
        setValue('executableRule', undefined, { shouldDirty: true });
        setValue('actorRule', undefined, { shouldDirty: true });

        // Force the user back through the wizard so they can't jump straight to review/save
        // without reconfiguring the wiped policy definition.
        if (hasPolicyData) {
            resetVisitedSteps(0);
        }
    }, [ setValue, hasPolicyData, createDialog, translate, resetVisitedSteps ]);

    const stepHooks = useMemo(() => {
        const isTenantScopeInvalid = (!tenantId || tenantId.trim() === '');
        return { disableNext: isTenantScopeInvalid };
    }, [ tenantId ]);

    useStepHooks(stepHooks);

    return {
        tenantId,
        hasPolicyData,
        tenants,
        isLoadingTenants,
        handleTenantChange,
        isTenantScoped,
        dropdownKey,
    };
};

export default useGovernancePolicyCreateScopeStepViewModel;

